import { HttpHeaders, httpResource } from '@angular/common/http';
import { inject, Injectable, Signal } from '@angular/core';
import { REQUIRE_AUTH_HEADER } from '@psu-pennstateit-swe/utils/security';
import { APP_PROPERTIES } from '../../environments/fortress.properties.model';
import { FortressPermission, FortressPermissionObject, FortressUser, Role } from './fortress.models';

@Injectable({
  providedIn: 'root',
})
export class FortressService {
  private baseUrl = inject(APP_PROPERTIES).fortress.url;
  private httpHeaders = new HttpHeaders({
    [REQUIRE_AUTH_HEADER]: 'true',
  });

  rolesResource = () =>
    httpResource<Role[]>(() => ({ url: `${this.baseUrl}roles`, headers: this.httpHeaders }), {
      parse: (response: unknown) => (response as Role[]).sort((a, b) => a.name.localeCompare(b.name)),
    });

  roleByNameResource = (roleName: Signal<string>) =>
    httpResource<Role>(() => ({ url: `${this.baseUrl}roles/${roleName()}`, headers: this.httpHeaders }), {
      parse: (response: unknown) => ({ ...(response as Role), type: 'ROLE' }),
    });

  roleUsersResource = (roleName: Signal<string>) =>
    httpResource<FortressUser[]>(
      () => ({ url: `${this.baseUrl}roles/${roleName()}/users`, headers: this.httpHeaders }),
      {
        parse: (response: unknown) =>
          (response as FortressUser[])
            .sort((a, b) => a.userId.localeCompare(b.userId))
            .map((user) => ({ ...user, type: 'USER' })),
      },
    );

  rolePermissionsResource = (roleName: Signal<string>) =>
    httpResource<FortressPermission[]>(
      () => ({ url: `${this.baseUrl}roles/${roleName()}/permissions`, headers: this.httpHeaders }),
      {
        parse: (response: unknown) =>
          (response as FortressPermission[])
            .sort((a, b) => a.abstractName.localeCompare(b.abstractName))
            .map((permission) => ({ ...permission, type: 'PERMISSION' })),
      },
    );

  userResource = (userId: Signal<string>) =>
    httpResource<FortressUser>(() => ({ url: `${this.baseUrl}users/${userId()}`, headers: this.httpHeaders }), {
      parse: (response: unknown) => ({ ...(response as FortressUser), type: 'USER' }),
    });

  userRolesResource = (userId: Signal<string>) =>
    httpResource<Role[]>(() => ({ url: `${this.baseUrl}users/${userId()}/roles`, headers: this.httpHeaders }), {
      parse: (response: unknown) =>
        (response as Role[]).sort((a, b) => a.name.localeCompare(b.name)).map((role) => ({ ...role, type: 'ROLE' })),
    });

  userPermissionsResource = (userId: Signal<string>) =>
    httpResource<FortressPermission[]>(
      () => ({ url: `${this.baseUrl}users/${userId()}/permissions`, headers: this.httpHeaders }),
      {
        parse: (response: unknown) =>
          (response as FortressPermission[])
            .sort((a, b) => a.abstractName.localeCompare(b.abstractName))
            .map((permission) => ({ ...permission, type: 'PERMISSION' })),
      },
    );

  permissionResource = (permObj: Signal<string>, permOp: Signal<string>) =>
    httpResource<FortressPermission>(
      () => ({
        url: `${this.baseUrl}permissions/objects/${permObj()}/operations/${permOp()}`,
        headers: this.httpHeaders,
      }),
      {
        parse: (response: unknown) => ({ ...(response as FortressPermission), type: 'PERMISSION' }),
      },
    );

  permissionRolesResource = (permObj: Signal<string>, permOp: Signal<string>) =>
    httpResource<Role[]>(
      () => ({
        url: `${this.baseUrl}permissions/objects/${permObj()}/operations/${permOp()}/roles`,
        headers: this.httpHeaders,
      }),
      {
        parse: (response: unknown) =>
          (response as Role[]).sort((a, b) => a.name.localeCompare(b.name)).map((role) => ({ ...role, type: 'ROLE' })),
      },
    );

  permissionUsersResource = (permObj: Signal<string>, permOp: Signal<string>) =>
    httpResource<FortressUser[]>(
      () => ({
        url: `${this.baseUrl}permissions/objects/${permObj()}/operations/${permOp()}/users`,
        headers: this.httpHeaders,
      }),
      {
        parse: (response: unknown) =>
          (response as FortressUser[])
            .sort((a, b) => a.userId.localeCompare(b.userId))
            .map((user) => ({ ...user, type: 'USER' })),
      },
    );

  permissionObjResource = (permObj: Signal<string>) =>
    httpResource<FortressPermissionObject>(
      () => ({ url: `${this.baseUrl}permissions/objects/${permObj()}`, headers: this.httpHeaders }),
      {
        parse: (response: unknown) => ({ ...(response as FortressPermissionObject), type: 'PERMISSION_OBJECT' }),
      },
    );

  permissionObjOperationsResource = (permObj: Signal<string>) =>
    httpResource<FortressPermission[]>(
      () => ({ url: `${this.baseUrl}permissions/objects/${permObj()}/operations`, headers: this.httpHeaders }),
      {
        parse: (response: unknown) =>
          (response as FortressPermission[])
            .sort((a, b) => a.opName.localeCompare(b.opName))
            .map((permission) => ({ ...permission, type: 'PERMISSION' })),
      },
    );
}
